import React, { useState, useEffect } from 'react';
import { Alert, Skeleton } from 'antd';
import { CheckCircleOutlined, SyncOutlined, LoadingOutlined, WarningOutlined, CloseCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import {
  type BrevoSyncEstadoResponse,
  type BrevoSyncEjecucion,
  type BrevoSyncRitmoPunto,
  computeFaseActual,
  transcurridoSegundos,
  formatDuracion,
  porcentajeFase,
} from '../../api/brevoSyncApi';

interface StatusBannerProps {
  estado: BrevoSyncEstadoResponse | null;
  ritmo?: BrevoSyncRitmoPunto[];
  loading: boolean;
}

type VisualState = 'idle' | 'initializing' | 'slow_load' | 'in_progress' | 'stuck' | 'completed_ok' | 'completed_errors' | 'failed';

function getVisualState(estado: BrevoSyncEstadoResponse | null, ritmo?: BrevoSyncRitmoPunto[]): VisualState {
  if (!estado) return 'idle';

  if (estado.running) {
    const r = estado.running;
    const elapsed = transcurridoSegundos(r);
    const fase = computeFaseActual(r);

    if (fase === 'loading') {
      return elapsed >= 300 ? 'slow_load' : 'initializing';
    }

    // Detección cuelgue
    if (ritmo && ritmo.length > 0 && elapsed > 300) {
      const ahora = new Date(r.ahora).getTime();
      const ultimoActivo = ritmo
        .filter(p => p.contactos > 0)
        .map(p => new Date(p.minuto).getTime())
        .reduce((a, b) => Math.max(a, b), 0);
      if (ultimoActivo > 0) {
        const minSinActividad = (ahora - ultimoActivo) / 60000;
        if (minSinActividad >= 3) return 'stuck';
      }
    }

    return 'in_progress';
  }

  if (estado.ultimaFinalizada) {
    const uf = estado.ultimaFinalizada;
    if (uf.estado === 'error') return 'failed';
    if (uf.faseAErrores > 0 || uf.faseBErrores > 0) return 'completed_errors';
    return 'completed_ok';
  }

  return 'idle';
}

function formatRelativeTime(isoDate: string, ahora: string): string {
  const diff = (new Date(ahora).getTime() - new Date(isoDate).getTime()) / 1000;
  if (diff < 60) return 'hace unos segundos';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} horas`;
  return `hace ${Math.floor(diff / 86400)} días`;
}

/**
 * Tick cada segundo para interpolar el tiempo transcurrido entre polls.
 * Calibrado con el reloj del servidor (r.ahora) para evitar drift por diferencia de relojes.
 */
function useLocalElapsed(r: BrevoSyncEjecucion | undefined): string {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!r) return;
    setTick(0);
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [r?.id, r?.ahora]);

  if (!r) return '';
  const serverElapsed = transcurridoSegundos(r);
  return formatDuracion(Math.max(0, serverElapsed + tick));
}

const StatusBanner: React.FC<StatusBannerProps> = ({ estado, ritmo, loading }) => {
  const liveElapsed = useLocalElapsed(estado?.running);

  if (loading && !estado) return <Skeleton active paragraph={{ rows: 1 }} style={{ marginBottom: 16 }} />;

  const vs = getVisualState(estado, ritmo);
  const r = estado?.running;
  const uf = estado?.ultimaFinalizada;

  const configs: Record<VisualState, { type: 'info' | 'success' | 'warning' | 'error'; icon: React.ReactNode; message: string; description: string }> = {
    idle: {
      type: 'success',
      icon: <CheckCircleOutlined />,
      message: uf ? `Última sincronización: ${formatRelativeTime(uf.fin!, uf.ahora)}` : 'Sin ejecuciones registradas',
      description: uf ? `${(uf.faseAExitos + uf.faseBExitos).toLocaleString('es')} contactos · ${formatDuracion(Math.floor((new Date(uf.fin!).getTime() - new Date(uf.inicio).getTime()) / 1000))} · ejecución ${new Date(uf.inicio).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}` : '',
    },
    initializing: {
      type: 'info',
      icon: <LoadingOutlined />,
      message: 'Cargando datos del CRM',
      description: r ? `Esta fase tarda unos 3 minutos · transcurrido ${liveElapsed}` : '',
    },
    slow_load: {
      type: 'warning',
      icon: <WarningOutlined />,
      message: 'La carga inicial está tardando más de lo habitual',
      description: r ? `Transcurrido ${liveElapsed}` : '',
    },
    in_progress: {
      type: 'info',
      icon: <SyncOutlined spin />,
      message: r ? (() => {
        const fase = computeFaseActual(r);
        const pct = fase === 'A'
          ? porcentajeFase(r.faseATotal, r.faseAExitos, r.faseAErrores)
          : porcentajeFase(r.faseBTotal, r.faseBExitos, r.faseBErrores);
        return `Fase ${fase} · ${pct != null ? pct + '%' : 'calculando…'}`;
      })() : 'En curso',
      description: r ? `Iniciada a las ${new Date(r.inicio).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} · transcurrido ${liveElapsed}` : '',
    },
    stuck: {
      type: 'warning',
      icon: <ClockCircleOutlined />,
      message: 'Sin progreso reciente',
      description: r ? `La sincronización lleva ${liveElapsed} en marcha` : '',
    },
    completed_ok: {
      type: 'success',
      icon: <CheckCircleOutlined />,
      message: 'Sincronización completada',
      description: uf ? `${(uf.faseAExitos + uf.faseBExitos).toLocaleString('es')} contactos · ${formatDuracion(Math.floor((new Date(uf.fin!).getTime() - new Date(uf.inicio).getTime()) / 1000))} · finalizada ${formatRelativeTime(uf.fin!, uf.ahora)}` : '',
    },
    completed_errors: {
      type: 'warning',
      icon: <WarningOutlined />,
      message: uf ? `Completada con ${uf.faseAErrores + uf.faseBErrores} errores` : 'Completada con errores',
      description: uf ? `${(uf.faseAExitos + uf.faseBExitos).toLocaleString('es')} contactos sincronizados · los errores se reintentarán en la próxima sincronización` : '',
    },
    failed: {
      type: 'error',
      icon: <CloseCircleOutlined />,
      message: 'La sincronización falló',
      description: uf?.errorDetalle ? uf.errorDetalle.split('\n')[0] : 'Error desconocido',
    },
  };

  const cfg = configs[vs];

  return (
    <Alert
      type={cfg.type}
      icon={cfg.icon}
      message={cfg.message}
      description={cfg.description}
      showIcon
      style={{ marginBottom: 16, borderRadius: 8 }}
    />
  );
};

export default StatusBanner;
